import type { AgentToolCall } from "@repo/components/src/shared/agent/types";

/**
 * The tools that write the draft. Shared with the refetch effect in `index.tsx` —
 * a section cannot be "Trabajando…" for a tool call that does not touch the
 * draft at all.
 */
export const DRAFT_WRITERS = new Set(["patch_draft", "propose_deliverables"]);

export type SectionStatus = "ready" | "writing" | "pending";

export type SectionStatuses = {
  products: SectionStatus;
  contents: SectionStatus;
  /** "Términos" combines the wizard's payments (step 4) and creator terms
   * (step 5) into one section — ready only once both are. */
  terms: SectionStatus;
};

/** The wizard step a `missing` line belongs to — the same prefix contract
 * `draftPanel/steps.ts` reads (`"stepTwo.products: ..."`). */
type DraftStepKey =
  | "stepOne"
  | "stepTwo"
  | "stepThree"
  | "stepFour"
  | "stepFive";

const DRAFT_STEP_KEYS: DraftStepKey[] = [
  "stepOne",
  "stepTwo",
  "stepThree",
  "stepFour",
  "stepFive",
];

const stepOf = (line: string): DraftStepKey | null =>
  DRAFT_STEP_KEYS.find((key) => line.startsWith(`${key}.`)) ?? null;

const isDraftWriterActive = (tools: AgentToolCall[]): boolean =>
  tools.some((tool) => DRAFT_WRITERS.has(tool.toolName) && !tool.done);

const statusFor = (ready: boolean, writing: boolean): SectionStatus => {
  if (ready) return "ready";
  return writing ? "writing" : "pending";
};

/**
 * A status per section of the preview, derived from the backend's own sense of
 * completeness (`missing`, straight off `DraftCompleteness`) rather than from
 * whether the draft JSON happens to be non-empty.
 *
 * That used to be the check, and it lied: several fields ship with a default the
 * wizard applies before the user ever touches them (`rightsOfUse` defaults to
 * "forever", for one), so "the draft has *a* value here" was true from the very
 * first render and every section read "Listo" before the agent had done anything.
 * `missing` is the one signal that actually reflects what the agent — and the
 * wizard's own validation — considers done.
 */
export const deriveSectionStatuses = (
  missing: string[],
  tools: AgentToolCall[],
  awaitingTurnRefetch: boolean,
): SectionStatuses => {
  const writing = isDraftWriterActive(tools) || awaitingTurnRefetch;
  const missingStep = (step: DraftStepKey): boolean =>
    missing.some((line) => stepOf(line) === step);

  return {
    products: statusFor(!missingStep("stepTwo"), writing),
    contents: statusFor(!missingStep("stepThree"), writing),
    terms: statusFor(
      !missingStep("stepFour") && !missingStep("stepFive"),
      writing,
    ),
  };
};
